/**
 * De reactietegel (ontwerp v4 §3, laatste vier bullets van "Vandaag").
 *
 * Staat vast onderaan de middenkolom; de kolom erboven scrolt eronder
 * door. Tabs "Aan de klant" en "Notitie", status rechts, bij mail de
 * adresregels en het onderwerp, bij WhatsApp en chat alleen het
 * tekstveld. De body groeit tot 34 procent van de hoogte en scrolt
 * daarna; er is **geen tekenlimiet**.
 *
 * **De tegel start ingeklapt** (JP, keuze c van 22-09): één regel met
 * links wat er klaarligt en rechts dezelfde knoppen als altijd. Klikken
 * op de regel, erin tabben of gaan typen vouwt hem open tot die 34
 * procent; nog een klik op de kop klapt hem weer in. Een andere kaart
 * begint weer ingeklapt en de stand wordt niet onthouden — anders staat
 * de tegel morgen open om een keuze van gisteren.
 *
 * De twee harde regels van dit scherm:
 *
 * 1. **Enter verstuurt nooit.** Enter roept `/netjes` aan en toont de
 *    versie met "Terug naar mijn tekst". Shift+Enter is een nieuwe
 *    regel. De backend kan het ook niet anders: `/netjes` heeft geen
 *    weg naar de wachtrij.
 * 2. **Versturen gaat door de wachtrij**, ook bij "nu". De groene balk
 *    telt af tot het moment waarop het bericht echt vertrekt; tot dan
 *    haalt Ongedaan maken het bericht én de gekoppelde actie terug.
 *
 * Een knop die niet kan, staat uit mét de reden ernaast. Nooit stil
 * verbergen, nooit een toast als enige uitleg.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
} from "react";

import {
  afrondenApi,
  kaartenApi,
  plaatshouderMelding,
  werkplekTegelApi,
  type BelGestart,
  type TegelConcept,
} from "@/lib/werkplek-api";

import { AfrondBalk, AfrondKnop, AfrondVoorstel, useAfronden } from "./Afronden";

import { Belbalk } from "./Belbalk";
import { Belvenster } from "./Belvenster";
import { Klaarstaand } from "./Klaarstaand";
import { kopregelTekst } from "./kopregel";
import { luisterNaarTegel, neemOpdracht, type TegelOpdracht } from "./tegelBrug";
import { abonneer as abonneerVoeding, neemVoorstel } from "../Klanten/antwoordBrug";
import { momentenNu, vandaagZeventienKan, vroegsteEigenTijd } from "./momenten";
import { wekkerReden } from "../Vandaag/wekkerTekst";
import "./tegel.css";

type Tab = "klant" | "notitie";

export interface Hoofdactie {
  /** Het id van de voorgestelde actie uit het Acties-menu. */
  id: string;
  /** "Crediteren € 12,95" — de knop wordt "<label> en versturen". */
  label: string;
}

interface Props {
  /** De zaak die openstaat. Zonder zaak toont de tegel een lege stand. */
  zaakId: string | null;
  /** De actie die JP net uit het Acties-menu koos (bouwer 1). */
  hoofdactie?: Hoofdactie | null;
  /** Naar de klantpagina springen (bouwer 4). */
  onKlantpagina?: (klantId: string) => void;
  /** Sessie 47: de hoofdactie is met het bericht uitgevoerd. Daarna hoort
   *  de knop terug te vallen naar gewoon versturen; na Ongedaan maken is
   *  de actie teruggedraaid en kan hij niet nog eens bevestigd worden. */
  onHoofdactieGebruikt?: () => void;
}

const STEMMEN_PER_KANAAL: Record<string, string[]> = {
  mail: ["sanne", "jp"],
  whatsapp: ["nova", "jp"],
  chat: ["nova", "jp"],
};

function stemLabel(stem: string): string {
  if (stem === "jp") return "JP";
  return stem.charAt(0).toUpperCase() + stem.slice(1);
}

function kanaalLabel(kanaal: string): string {
  if (kanaal === "mail") return "mail";
  if (kanaal === "whatsapp") return "WhatsApp";
  return "chat";
}

function klokje(iso: string): string {
  // Expliciet Amsterdam: momenten reizen als UTC en JP leest
  // Amsterdamse kloktijd (API-contract §1). Zonder deze regel volgt de
  // tijd de zone van de browser, en dat is toevallig goed zolang
  // iedereen hier zit.
  return new Date(iso).toLocaleTimeString("nl-NL", {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Europe/Amsterdam",
  });
}

function foutTekst(fout: unknown): string {
  const detail = (
    fout as { response?: { data?: { detail?: unknown } } } | undefined
  )?.response?.data?.detail;
  if (typeof detail === "string" && detail.trim()) return detail;
  return "Er ging iets mis. Probeer het opnieuw.";
}

export function Reactietegel({
  zaakId,
  hoofdactie,
  onKlantpagina,
  onHoofdactieGebruikt,
}: Props) {
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<Tab>("klant");

  // Het antwoord zoals het nu in de tegel staat.
  const [stem, setStem] = useState<string | null>(null);
  const [tekst, setTekst] = useState("");
  const [onderwerp, setOnderwerp] = useState("");
  const [aan, setAan] = useState("");
  const [cc, setCc] = useState("");
  const [bcc, setBcc] = useState("");
  const [ccAan, setCcAan] = useState(false);
  const [bccAan, setBccAan] = useState(false);
  const [bijlageIds, setBijlageIds] = useState<string[]>([]);
  const [bewerken, setBewerken] = useState(false);
  const [aangepast, setAangepast] = useState(false);
  const [sanneLeert, setSanneLeert] = useState(true);
  const [moment, setMoment] = useState("nu");
  const [eigenTijd, setEigenTijd] = useState("");
  const [notitie, setNotitie] = useState("");
  const [bezig, setBezig] = useState(false);
  // "Nieuw bericht aan deze klant": de enige weg langs de grendel op een
  // afgeronde zaak. Blijft per kaart staan en gaat uit zodra JP een
  // andere kaart kiest.
  const [nieuwBericht, setNieuwBericht] = useState(false);
  // Ingeklapt of open (JP, keuze c van 22-09). Start dicht, wordt niet
  // onthouden en gaat bij een andere kaart weer dicht.
  const [open, setOpen] = useState(false);
  const tekstRef = useRef<HTMLTextAreaElement>(null);
  // Kwam de focus van een muisklik? Dan opent `onFocus` niet, want de
  // klik erna zou hem meteen weer dichtklappen: mousedown geeft focus
  // vóór het click-event. Alleen met Tab erin openen is de bedoeling.
  const muisRef = useRef(false);

  // Enter-flow: de nette versie naast het origineel.
  const [netjes, setNetjes] = useState<{
    netjes: string;
    origineel: string;
  } | null>(null);

  // Na versturen: de groene balk.
  const [verstuurd, setVerstuurd] = useState<{
    wachtrijId: string;
    ongedaanTot: string;
    actieRegel: string | null;
  } | null>(null);
  const [nu, setNu] = useState(() => Date.now());

  // Bellen.
  const [belvensterOpen, setBelvensterOpen] = useState(false);
  const [belKlant, setBelKlant] = useState<string | null>(null);
  // Sessie 47 (9d): waar de knop stond; de eigen knop in de tegel is "tegel".
  const [belBron, setBelBron] = useState<string>("tegel");
  const [gesprek, setGesprek] = useState<BelGestart | null>(null);

  const concept = useQuery({
    queryKey: ["werkplek", "concept", zaakId],
    queryFn: () => werkplekTegelApi.concept(zaakId as string),
    enabled: Boolean(zaakId),
    // Sessie 46 (B4): terwijl Sanne of Nova een voorstel schrijft, kijkt
    // de tegel elke drie seconden of het er al is.
    refetchInterval: (query) =>
      query.state.data?.voorstel_stand === "bezig" ? 3000 : false,
  });

  const data: TegelConcept | undefined = concept.data;

  // Sessie 47 (deel 2): afronden zonder antwoord, met ongedaan maken.
  const afronden = useAfronden(zaakId ?? null);
  const tochAntwoorden = useMutation({
    mutationFn: (id: string) => afrondenApi.tochAntwoorden(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["werkplek", "concept", zaakId],
      });
    },
  });
  const kanaal = data?.kanaal ?? "mail";
  // Een telefoonzaak of systeemkaart heeft geen gesprek om op te
  // antwoorden (sessie 46): dan werkt alleen de notitie.
  const zonderGesprek = kanaal === "telefoon" || kanaal === "geen";

  // Sessie 46 (B4): geen voorstel en nog niet gevraagd? Dan vraagt de
  // tegel er één aan zodra de zaak opent. Eén keer per zaak; er wordt
  // niets verstuurd.
  const gevraagdVoor = useRef<string | null>(null);
  const vraagVoorstel = useMutation({
    mutationFn: (id: string) => werkplekTegelApi.vraagVoorstel(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["werkplek", "concept", zaakId],
      });
    },
  });
  useEffect(() => {
    if (!data || !zaakId || gevraagdVoor.current === zaakId) return;
    if (data.afgerond || zonderGesprek) return;
    if (data.bron !== "leeg" || data.voorstel_stand !== "geen") return;
    if (data.aan.length === 0 && data.kanaal === "mail") return;
    gevraagdVoor.current = zaakId;
    vraagVoorstel.mutate(zaakId);
  }, [data, zaakId, zonderGesprek, vraagVoorstel]);

  // Het concept in de velden zetten zodra het binnen is. Bewust op
  // zaak-id en niet op elk antwoord: anders overschrijft een
  // achtergrondverversing wat JP net heeft getypt.
  const geladenVoor = useRef<string | null>(null);
  // De tekst zoals hij het laatst van de server kwam. Komt er later een
  // voorstel binnen (sessie 46, B4: Nova schrijft op de achtergrond) en
  // heeft JP nog niets getypt, dan schuift het voorstel erin. Heeft hij
  // wél getypt, dan blijft zijn tekst staan.
  const serverTekst = useRef<string>("");
  const afgerondVoor = useRef<boolean>(false);
  useEffect(() => {
    if (!data) return;
    if (geladenVoor.current === data.zaak_id) {
      // Sessie 48 (B1): ook een tekst die leeg wordt volgt de server. Na
      // "Afronden zonder antwoord" toonde de tegel de al verstuurde mail
      // (alleen-lezen); na "Ongedaan maken" is het concept weer leeg, maar
      // die mail bleef als bewerkbaar concept staan. Heeft JP zelf iets
      // getypt, dan blijft zijn tekst staan.
      if (data.tekst !== serverTekst.current) {
        const vorige = serverTekst.current;
        serverTekst.current = data.tekst;
        setTekst((huidig) => (huidig === vorige ? data.tekst : huidig));
      }
      // Wisselt de zaak tussen afgerond en open, dan terug naar de stand
      // van de server: niet bewerken, geen nette versie, geen balk.
      const afgerondNu = Boolean(data.afgerond);
      if (afgerondVoor.current !== afgerondNu) {
        afgerondVoor.current = afgerondNu;
        setBewerken(false);
        setAangepast(false);
        setNetjes(null);
        setNieuwBericht(false);
      }
      return;
    }
    afgerondVoor.current = Boolean(data.afgerond);
    geladenVoor.current = data.zaak_id;
    serverTekst.current = data.tekst;
    setStem(data.stem);
    setTekst(data.tekst);
    setOnderwerp(data.onderwerp ?? "");
    setAan(data.aan.join(", "));
    setCc(data.cc.join(", "));
    setBcc(data.bcc.join(", "));
    setCcAan(data.cc.length > 0);
    setBccAan(data.bcc.length > 0);
    setBijlageIds([]);
    setBewerken(false);
    setAangepast(false);
    setNetjes(null);
    setVerstuurd(null);
    setMoment("nu");
    setTab("klant");
    setNieuwBericht(false);
  }, [data]);

  // Een andere kaart begint weer ingeklapt. Bewust op `zaakId` en niet
  // op `data`: het antwoord van de vorige zaak staat er dan nog, en de
  // tegel hoort meteen dicht te zijn, niet pas als het nieuwe concept
  // binnen is.
  useEffect(() => {
    setOpen(false);
    muisRef.current = false;
  }, [zaakId]);

  // Deze effecten staan bewust ná het laden en het inklappen: in één
  // commit winnen ze dan van allebei.
  const geladenZaak = data?.zaak_id ?? null;
  // Sessie 46 (B1, B2): het klantblok en "Nieuw bericht" openen de tegel.
  useEffect(() => {
    function voerUit(opdracht: TegelOpdracht) {
      if (opdracht.zaakId !== null && opdracht.zaakId !== zaakId) return;
      neemOpdracht(zaakId);
      if (opdracht.soort === "bel") {
        setBelKlant(opdracht.klantId);
        setBelBron(opdracht.bron ?? "tegel");
        setBelvensterOpen(true);
        return;
      }
      setOpen(true);
      setTab("klant");
      if (opdracht.aan) setAan(opdracht.aan);
      // Sessie 47 (5b): de mail die Sanne bij "Retour afronden" schreef.
      if (opdracht.tekst) {
        // Er staat al een tekst klaar: geen tweede voorstel aanvragen.
        gevraagdVoor.current = zaakId;
        setTekst(opdracht.tekst);
        setNieuwBericht(true);
        setBewerken(true);
      }
      if (opdracht.onderwerp) setOnderwerp(opdracht.onderwerp);
      window.setTimeout(() => tekstRef.current?.focus(), 0);
    }
    // Een wachtende opdracht pas uitvoeren als het concept van deze zaak
    // er is: anders overschrijft het laden de stand die hij zet.
    if (geladenZaak === zaakId) {
      const wachtend = neemOpdracht(zaakId);
      if (wachtend) voerUit(wachtend);
    }
    return luisterNaarTegel(voerUit);
  }, [zaakId, geladenZaak]);

  // Sessie 46 (B9): "Zet in antwoord" uit het voedingsblok plakt in de
  // tegel van een zaak van diezelfde klant.
  useEffect(() => {
    function plak() {
      const detail = queryClient.getQueryData<{ klant?: { id?: string } | null }>([
        "werkplek",
        "zaak",
        zaakId,
      ]);
      const klantId = detail?.klant?.id;
      if (!klantId) return;
      const voorstel = neemVoorstel(klantId);
      if (!voorstel) return;
      setTekst((huidig) => (huidig.trim() ? `${huidig}\n\n${voorstel.tekst}` : voorstel.tekst));
      setOpen(true);
      setTab("klant");
    }
    if (geladenZaak === zaakId) plak();
    return abonneerVoeding(() => {
      if (geladenZaak === zaakId) plak();
    });
  }, [zaakId, geladenZaak, queryClient]);


  // Het tekstveld begint op vier regels en groeit met de inhoud mee
  // (bijschaven 22-09). De bovengrens is de body zelf: die scrolt al op
  // 34% van de hoogte, dus een veld dat verder groeit duwt niets weg.
  useLayoutEffect(() => {
    const el = tekstRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, [tekst, tab, zaakId, open]);

  // De aftelling van de groene balk.
  useEffect(() => {
    if (!verstuurd) return;
    const tik = window.setInterval(() => setNu(Date.now()), 500);
    return () => window.clearInterval(tik);
  }, [verstuurd]);

  const secondenOver = useMemo(() => {
    if (!verstuurd) return 0;
    return Math.max(
      0,
      Math.ceil((new Date(verstuurd.ongedaanTot).getTime() - nu) / 1000),
    );
  }, [verstuurd, nu]);

  // Wat er werkelijk verstuurd wordt: de nette versie als die er is,
  // anders de eigen tekst. Eén bron, zodat de knop en de verzending
  // nooit uit elkaar kunnen lopen.
  const teVersturenTekst = netjes ? netjes.netjes : tekst;

  // Sessie 48 (G1, ontwerp §3 "wekker gezet"): de wekker ontstaat pas als
  // de wachtrij het bericht verstuurt, na het ongedaan-venster (elke 10 s).
  // Dan halen we de zaak één keer opnieuw op en noemt de balk de wekker.
  const zaakDetail = useQuery({
    queryKey: ["werkplek", "zaak", zaakId],
    queryFn: () => kaartenApi.detail(zaakId as string),
    enabled: Boolean(zaakId && verstuurd),
  });
  const vensterVoorbij = Boolean(verstuurd) && secondenOver === 0;
  useEffect(() => {
    if (!vensterVoorbij || !zaakId) return;
    const t = window.setTimeout(() => {
      void queryClient.invalidateQueries({ queryKey: ["werkplek", "zaak", zaakId] });
    }, 12_000);
    return () => window.clearTimeout(t);
  }, [vensterVoorbij, zaakId, queryClient]);
  const gezetteWekker =
    vensterVoorbij && zaakDetail.data?.wekker?.status === "loopt" ? zaakDetail.data.wekker : null;

  // ── Mutaties ──────────────────────────────────────────────────────

  const netjesMutatie = useMutation({
    mutationFn: (invoer: string) =>
      werkplekTegelApi.netjes(zaakId as string, {
        tekst: invoer,
        kanaal,
        stem: stem ?? "jp",
      }),
    onSuccess: (uitkomst) => {
      setNetjes({ netjes: uitkomst.netjes, origineel: uitkomst.origineel });
      if (uitkomst.onderwerp_voorstel && !onderwerp) {
        setOnderwerp(uitkomst.onderwerp_voorstel);
      }
    },
  });

  const handtekeningMutatie = useMutation({
    mutationFn: (nieuweStem: string) =>
      werkplekTegelApi.handtekening(nieuweStem),
    onSuccess: (uitkomst) => {
      // Alleen de handtekening wisselt; de tekst blijft staan.
      queryClient.setQueryData<TegelConcept>(
        ["werkplek", "concept", zaakId],
        (oud) =>
          oud
            ? {
                ...oud,
                handtekening_html: uitkomst.handtekening_html,
                handtekening_tekst: uitkomst.handtekening_tekst,
              }
            : oud,
      );
    },
  });

  // Sessie 48 (B1): de server weigert een tekst die in dit gesprek al
  // verstuurd is. Dan vraagt de tegel het expliciet, in plaats van hem
  // stil nog een keer te sturen of een kale foutmelding te tonen.
  const [plekMelding, setPlekMelding] = useState<string | null>(null);
  const [dubbel, setDubbel] = useState<{
    metActie: boolean;
    uitleg: string;
  } | null>(null);

  const verstuurMutatie = useMutation({
    mutationFn: ({ metActie, bevestig }: { metActie: boolean; bevestig?: boolean }) =>
      werkplekTegelApi.versturen(zaakId as string, {
        kanaal,
        aan: splits(aan),
        cc: ccAan ? splits(cc) : [],
        bcc: bccAan ? splits(bcc) : [],
        onderwerp: kanaal === "mail" ? onderwerp || null : null,
        tekst: teVersturenTekst,
        stem: stem ?? "sanne",
        bijlage_ids: bijlageIds,
        moment: moment === "kies" ? eigenTijd : moment,
        actie_id: metActie && hoofdactie ? hoofdactie.id : null,
        sanne_leert: aangepast && sanneLeert,
        nieuw_bericht: nieuwBericht,
        bevestig_dubbel: Boolean(bevestig),
      }),
    onSuccess: (uitkomst, { metActie }) => {
      setBezig(false);
      setDubbel(null);
      if (metActie && hoofdactie) onHoofdactieGebruikt?.();
      setVerstuurd({
        wachtrijId: uitkomst.wachtrij_id,
        ongedaanTot: uitkomst.ongedaan_tot,
        actieRegel: uitkomst.actie_regel,
      });
      setNu(Date.now());
      void queryClient.invalidateQueries({
        queryKey: ["werkplek", "tijdlijn", zaakId],
      });
      // Sessie 48 (B2): de copiloot leest de nieuwe stand van de zaak.
      void queryClient.invalidateQueries({
        queryKey: ["werkplek", "copiloot", "gesprek"],
      });
      // De strook "staat klaar" leest de wachtrij; die moet meteen de
      // nieuwe rij tonen, ook bij "morgen 08:00".
      void queryClient.invalidateQueries({
        queryKey: ["werkplek", "wachtrij", zaakId],
      });
    },
    onError: (fout, { metActie }) => {
      setBezig(false);
      const antwoord = (
        fout as { response?: { status?: number; data?: { code?: string; detail?: string } } }
      )?.response;
      if (antwoord?.status === 409 && antwoord.data?.code === "al_verstuurd") {
        setDubbel({
          metActie,
          uitleg:
            antwoord.data.detail ??
            "Deze tekst is in dit gesprek al verstuurd.",
        });
      }
    },
  });

  const ongedaanMutatie = useMutation({
    mutationFn: (wachtrijId: string) => werkplekTegelApi.ongedaan(wachtrijId),
    onSuccess: () => {
      setVerstuurd(null);
      void queryClient.invalidateQueries({
        queryKey: ["werkplek", "tijdlijn", zaakId],
      });
      void queryClient.invalidateQueries({
        queryKey: ["werkplek", "wachtrij", zaakId],
      });
    },
  });

  const notitieMutatie = useMutation({
    mutationFn: (inhoud: string) =>
      werkplekTegelApi.notitie(zaakId as string, inhoud),
    onSuccess: () => {
      setNotitie("");
      void queryClient.invalidateQueries({
        queryKey: ["werkplek", "tijdlijn", zaakId],
      });
    },
  });

  // ── Afgeleide waarden ─────────────────────────────────────────────

  // "Vandaag 17:00" is na 17:00 geen keuze meer. Bewust bij elke render
  // opnieuw gerekend en niet gememoiseerd: de klok loopt door terwijl
  // JP typt, en een gememoiseerde waarde zou de keuze open houden
  // nadat het moment verstreken is.
  // Is het antwoord op deze zaak al de deur uit? Dan staat de tekst
  // alleen-lezen en zijn er geen versturen-knoppen — tot JP zelf op
  // "Nieuw bericht aan deze klant" drukt (bijschaven 22-09).
  const isAfgerond = Boolean(data?.afgerond) && !nieuwBericht;

  const zeventienKan = vandaagZeventienKan();
  // Sessie 48 (B3): een eigen tijd die al geweest is, kan ook niet.
  const eigenTijdVoorbij =
    moment === "kies" && Boolean(eigenTijd) && eigenTijd < vroegsteEigenTijd();
  const momentOngeldig =
    (moment === "kies" && !eigenTijd) ||
    eigenTijdVoorbij ||
    (moment === "vandaag_17" && !zeventienKan);
  const geenOntvanger = kanaal === "mail" && splits(aan).length === 0;
  const legeTekst = teVersturenTekst.trim().length === 0;
  const redenUit = legeTekst
    ? "Er staat nog geen tekst in het bericht."
    : geenOntvanger
      ? "Vul een ontvanger in bij Aan."
      : moment === "vandaag_17" && !zeventienKan
        ? "Vandaag 17:00 is al geweest. Kies 'nu', 'morgen 08:00' of een eigen tijd."
        : eigenTijdVoorbij
          ? "Die tijd is al geweest. Kies 'nu' of een tijd in de toekomst."
          : momentOngeldig
            ? "Kies eerst een tijdstip."
            : null;
  const kanVersturen = !redenUit && !bezig && Boolean(zaakId) && !isAfgerond;

  // Dezelfde reden, maar dan op de kopregel van een ingeklapte tegel.
  // Een knop die niet kan hoort altijd te zeggen waarom — ook als de
  // tegel dicht is. Hij krimpt mee en draagt zijn volle tekst in de
  // tooltip; openklappen toont hem onverkort onderin.
  // Afgerond zonder één mail uit het portaal: dan is er niets om
  // alleen-lezen te tonen en zou het tekstveld een leeg vak zijn. De
  // kopregel zegt al wat er aan de hand is (JP, sessie 41).
  const afgerondZonderTekst = isAfgerond && !tekst.trim();

  // Kort genoeg om op twee regels te passen naast de knop (JP, punt 11
  // van 22-09). De volledige zin hangt als tip aan de knop zelf; wat op
  // de kopregel staat mag nooit halverwege afgekapt worden, want dan is
  // de reden onleesbaar.
  const redenUitKort = isAfgerond
    ? "Afgerond. Nog iets sturen begint een nieuw bericht."
    : redenUit;

  function versturen(metActie: boolean) {
    if (!kanVersturen) return;
    // Fixronde 52b: een open plek ([KORTINGSCODE], <code>) gaat nooit de
    // deur uit. De melding blijft staan tot JP de tekst aanpast.
    const openPlek = plaatshouderMelding(tekst);
    if (openPlek) {
      setPlekMelding(openPlek);
      return;
    }
    setPlekMelding(null);
    setDubbel(null);
    verstuurMutatie.mutate({ metActie });
    // Pas een tel later uitzetten: `disabled` in dezelfde tel annuleert
    // de handeling eronder (les 17-09).
    window.setTimeout(() => setBezig(true), 0);
  }

  // ── Inklappen ─────────────────────────────────────────────────

  const kopTekst = kopregelTekst({
    tab,
    afgerond: isAfgerond,
    afgerondZonderMail: Boolean(data?.afgerond_zonder_mail),
    verstuurdDoor: data?.verstuurd_door ?? null,
    verstuurdOm: data?.verstuurd_op ? klokje(data.verstuurd_op) : null,
    bron: data?.bron ?? "leeg",
    voorstelBezig: data?.voorstel_stand === "bezig",
    voorstelDoor: kanaal === "mail" ? "sanne" : "nova",
    afgerondWaarom: data?.afgerond_waarom ?? null,
    telefoon: kanaal === "telefoon",
    afrondenLabel:
      data?.voorstel_stand === "afronden" && !isAfgerond
        ? data?.afronden_label ?? null
        : null,
  });

  function focusTekstveld() {
    // Een tel later: het veld bestaat pas nadat React de body heeft
    // gerenderd.
    window.setTimeout(() => tekstRef.current?.focus(), 0);
  }

  function opKopToets(e: KeyboardEvent<HTMLButtonElement>) {
    // Gaan typen op de dichte tegel opent hem en zet de cursor in het
    // tekstveld. Enter en spatie niet: die zijn de klik op deze knop.
    if (open) return;
    if (e.key.length !== 1 || e.key === " ") return;
    if (e.metaKey || e.ctrlKey || e.altKey) return;
    e.preventDefault();
    setOpen(true);
    const teken = e.key;
    window.setTimeout(() => {
      const veld = tekstRef.current;
      if (!veld) return;
      veld.focus();
      // Alleen invoegen als het veld schrijfbaar is; anders raakt het
      // teken zoek in een alleen-lezen tekst.
      if (!veld.readOnly && tab === "klant") {
        setTekst((huidig) => huidig + teken);
      } else if (!veld.readOnly) {
        setNotitie((huidig) => huidig + teken);
      }
    }, 0);
  }

  function toetsInTekstveld(e: KeyboardEvent<HTMLTextAreaElement>) {
    // Enter verstuurt nooit. Shift+Enter is een nieuwe regel; Enter
    // alleen laat de AI de tekst netjes maken, die JP daarna goedkeurt.
    if (e.key !== "Enter" || e.shiftKey) return;
    e.preventDefault();
    if (!zaakId || !tekst.trim() || netjesMutatie.isPending) return;
    netjesMutatie.mutate(tekst);
  }

  // De knoppen rechtsonder. Eén definitie, twee plaatsen: bij een
  // ingeklapte tegel staan ze op de kopregel, bij een open tegel in
  // de knoppenrij eronder. Nooit allebei tegelijk, want dan zou er
  // twee keer een “Alleen versturen” in beeld staan.
  const rechterKnoppen =
    tab === "klant" && zonderGesprek && !isAfgerond ? (
      // Sessie 48 (A): een telefoonzaak heeft niets om te versturen. Geen
      // "Alleen versturen" en geen verzendmoment, wel terugbellen en
      // afronden.
      <div className="wp-tegel-rij-rechts" data-tegel-telefoon>
        <AfrondKnop
          staat={afronden}
          voorstelReden={data?.afronden_reden ?? null}
        />
        {kanaal === "telefoon" ? (
          <button
            type="button"
            className="wp-knop wp-knop-primair"
            data-testid="wp-tegel-terugbellen"
            onClick={() => {
              setBelBron("tegel");
              setBelvensterOpen(true);
              setOpen(true);
            }}
          >
            Terugbellen
          </button>
        ) : null}
      </div>
    ) : tab === "klant" ? (
      <div className="wp-tegel-rij-rechts">
        <label htmlFor="wp-moment" className="wp-sr" hidden={isAfgerond}>
          Wanneer versturen
        </label>
        <select
          hidden={isAfgerond}
          id="wp-moment"
          value={moment}
          onChange={(e) => setMoment(e.target.value)}
        >
          {momentenNu().map((keuze) => (
            <option key={keuze.waarde} value={keuze.waarde}>
              {keuze.label}
            </option>
          ))}
        </select>
        {moment === "kies" ? (
          <>
            <label htmlFor="wp-eigentijd" className="wp-sr">
              Eigen tijdstip
            </label>
            <input
              id="wp-eigentijd"
              type="datetime-local"
              min={vroegsteEigenTijd()}
              value={eigenTijd}
              onChange={(e) => setEigenTijd(e.target.value)}
            />
          </>
        ) : null}

        {netjes ? (
          <button
            type="button"
            className="wp-knop wp-tegel-knop-klein"
            onClick={() => setNetjes(null)}
          >
            Terug naar mijn tekst
          </button>
        ) : null}

        {isAfgerond ? (
          <button
            type="button"
            className="wp-knop wp-knop-primair"
            data-testid="wp-tegel-nieuw-bericht"
            onClick={() => {
              setNieuwBericht(true);
              setTekst("");
              setNetjes(null);
              setBewerken(true);
              setAangepast(false);
              // Een nieuw bericht schrijf je in een open tegel.
              setOpen(true);
              focusTekstveld();
            }}
          >
            Nieuw bericht aan deze klant
          </button>
        ) : (
          <>
            <AfrondKnop
              staat={afronden}
              voorstelReden={data?.afronden_reden ?? null}
            />
            <button
              type="button"
              className="wp-knop"
              onClick={() => versturen(false)}
              disabled={!kanVersturen}
              title={redenUit ?? undefined}
            >
              Alleen versturen
            </button>
            {hoofdactie ? (
              <button
                type="button"
                className="wp-knop wp-knop-primair"
                onClick={() => versturen(true)}
                disabled={!kanVersturen}
                title={redenUit ?? undefined}
              >
                {hoofdactie.label} en versturen
              </button>
            ) : null}
          </>
        )}
      </div>
    ) : (
      <div className="wp-tegel-rij-rechts">
        <button
          type="button"
          className="wp-knop wp-knop-primair"
          onClick={() => notitieMutatie.mutate(notitie)}
          disabled={!notitie.trim() || notitieMutatie.isPending}
        >
          Notitie opslaan
        </button>
      </div>
    );

  // ── Lege stand ────────────────────────────────────────────────────

  if (!zaakId) {
    return (
      <div className="wp-tegel-voet">
        <div className="wp-tegel">
          <div className="wp-tegel-body">
            <p className="wp-tegel-uitleg">
              Kies een kaart om te antwoorden. De reactietegel vult zich met het
              voorstel van Sanne zodra er een zaak openstaat.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const stemmen = STEMMEN_PER_KANAAL[kanaal] ?? ["sanne", "jp"];

  return (
    <div className="wp-tegel-voet">
      {gesprek ? (
        <Belbalk
          gesprek={gesprek}
          onKlantpagina={onKlantpagina}
          onNotitie={() => setTab("notitie")}
          onSluiten={() => setGesprek(null)}
        />
      ) : null}

      {belvensterOpen ? (
        <div style={{ marginBottom: 8 }}>
          <Belvenster
            zaakId={zaakId}
            klantId={belKlant}
            bron={belBron}
            onGestart={(nieuw) => {
              setGesprek(nieuw);
              setBelvensterOpen(false);
            }}
            onSluiten={() => setBelvensterOpen(false)}
          />
        </div>
      ) : null}

      {dubbel ? (
        <div
          className="wp-tegel-dubbel"
          role="alertdialog"
          aria-label="Al verstuurd"
          data-testid="wp-tegel-dubbel"
        >
          <p>{dubbel.uitleg}</p>
          <div className="wp-tegel-dubbel-knoppen">
            <button
              type="button"
              className="wp-knop"
              data-testid="wp-tegel-dubbel-nee"
              onClick={() => setDubbel(null)}
            >
              Niet versturen
            </button>
            <button
              type="button"
              className="wp-knop wp-knop-gevaar"
              data-testid="wp-tegel-dubbel-ja"
              onClick={() => {
                const metActie = dubbel.metActie;
                setDubbel(null);
                verstuurMutatie.mutate({ metActie, bevestig: true });
              }}
            >
              Toch nog een keer versturen
            </button>
          </div>
        </div>
      ) : null}

      {/* Wat er klaarstaat, en tot wanneer het terug kan. Blijft staan
          zolang er iets in de wachtrij zit — ook als de groene balk van
          het versturen zelf allang weg is (plak 2.4). */}
      <Klaarstaand
        zaakId={zaakId}
        onGewijzigd={() => {
          setVerstuurd(null);
          void queryClient.invalidateQueries({
            queryKey: ["werkplek", "tijdlijn", zaakId],
          });
        }}
      />

      <AfrondBalk staat={afronden} />

      {verstuurd ? (
        <div className="wp-verstuurd" role="status" aria-live="polite">
          <span>
            <strong>Verstuurd {klokje(verstuurd.ongedaanTot)}</strong>
            {verstuurd.actieRegel ? ` · ${verstuurd.actieRegel}` : ""}
            {gezetteWekker ? (
              <span data-testid="wp-verstuurd-wekker">
                {` · wekker gezet: ${wekkerReden(gezetteWekker.reden)}, ${new Date(
                  gezetteWekker.afloopt_op,
                ).toLocaleString("nl-NL", {
                  timeZone: "Europe/Amsterdam",
                  weekday: "short",
                  day: "numeric",
                  month: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                })}`}
              </span>
            ) : null}{" "}
            · dit blijft staan tot je een andere kaart kiest.
          </span>
          {secondenOver > 0 ? (
            <button
              type="button"
              className="wp-knop wp-tegel-knop-klein"
              style={{ marginLeft: "auto" }}
              onClick={() => ongedaanMutatie.mutate(verstuurd.wachtrijId)}
              disabled={ongedaanMutatie.isPending}
            >
              Ongedaan maken{" "}
              <span className="wp-verstuurd-tel">{secondenOver}</span>
            </button>
          ) : (
            <span style={{ marginLeft: "auto", color: "var(--wp-tekst2)" }}>
              Het bericht is de deur uit; terugnemen kan niet meer.
            </span>
          )}
          {ongedaanMutatie.isError ? (
            <p className="wp-tegel-fout" style={{ flexBasis: "100%" }}>
              {foutTekst(ongedaanMutatie.error)}
            </p>
          ) : null}
        </div>
      ) : null}

      <div className="wp-tegel" data-open={open ? "ja" : "nee"}>
        {/* De kopregel. Ingeklapt is dit de hele tegel: links wat er
            klaarligt, rechts dezelfde knoppen als altijd (JP, keuze c
            van 22-09). Een klik toggelt; met Tab erin of gaan typen
            gaat hij open. */}
        <div className="wp-tegel-kop">
          <button
            type="button"
            className="wp-tegel-kopknop"
            data-testid="wp-tegel-kop"
            aria-expanded={open}
            aria-controls="wp-tegel-body"
            onPointerDown={() => {
              muisRef.current = true;
            }}
            onFocus={() => {
              if (muisRef.current) return;
              setOpen(true);
            }}
            onBlur={() => {
              muisRef.current = false;
            }}
            onClick={() => {
              setOpen(!open);
              muisRef.current = false;
            }}
            onKeyDown={opKopToets}
          >
            <span className="wp-tegel-pijl" aria-hidden="true">
              {open ? "\u25be" : "\u25b8"}
            </span>
            {kopTekst}
          </button>
          {!open && tab === "klant" && redenUitKort ? (
            <span className="wp-tegel-kop-reden" title={redenUitKort}>
              {redenUitKort}
            </span>
          ) : null}
          {!open ? (
            <div className="wp-tegel-kop-rechts">{rechterKnoppen}</div>
          ) : null}
        </div>

        <div
          className="wp-tegel-tabs"
          role="tablist"
          aria-label="Reactie"
          hidden={!open}
        >
          <button
            type="button"
            role="tab"
            aria-selected={tab === "klant"}
            className="wp-tegel-tab"
            onClick={() => setTab("klant")}
          >
            Aan de klant
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === "notitie"}
            className="wp-tegel-tab"
            onClick={() => setTab("notitie")}
          >
            Notitie
          </button>
          <span className="wp-tegel-status">
            {data?.voorstel_stand === "afronden" && !isAfgerond ? (
              <span className="wp-chip wp-chip-grijs" data-testid="wp-tegel-afrond-chip">
                voorstel: afronden
              </span>
            ) : data?.bron === "sanne" ? (
              <span className="wp-chip wp-chip-grijs">voorstel van Sanne</span>
            ) : null}
            {data?.bron === "nova" ? (
              <span className="wp-chip wp-chip-grijs">voorstel van Nova</span>
            ) : null}
            {data?.voorstel_stand === "bezig" ? (
              <span className="wp-chip wp-chip-grijs" data-testid="wp-tegel-voorstel-bezig">
                {kanaal === "mail" ? "Sanne" : "Nova"} schrijft een voorstel…
              </span>
            ) : null}
            {data?.voorstel_stand === "invullen" ? (
              <span
                className="wp-chip wp-chip-invullen"
                data-testid="wp-tegel-vul-nog-in"
                title={data.voorstel_reden ?? undefined}
              >
                vul nog in
              </span>
            ) : null}
            {data?.voorstel_stand === "mislukt" && data?.bron === "leeg" && zaakId ? (
              <button
                type="button"
                className="wp-knop wp-tegel-knop-klein"
                onClick={() => vraagVoorstel.mutate(zaakId)}
                title={data.voorstel_reden ?? undefined}
              >
                Voorstel opnieuw proberen
              </button>
            ) : null}
            {data?.bron === "wekker" ? (
              <span className="wp-chip wp-chip-wekker">
                follow-up, automatisch klaargezet
              </span>
            ) : null}
            {zonderGesprek ? null : (
              <span className={`wp-chip wp-chip-${kanaal}`}>
                {kanaalLabel(kanaal)}
              </span>
            )}
            {/* Sessie 48 (Simulator): een telefoonzaak heeft geen tekst om
                aan te passen. */}
            {tab === "klant" && !isAfgerond && !zonderGesprek ? (
              <button
                type="button"
                className="wp-knop wp-tegel-knop-klein"
                aria-pressed={bewerken}
                onClick={() => {
                  if (bewerken) setAangepast(true);
                  setBewerken(!bewerken);
                }}
              >
                {bewerken ? "Klaar" : "Aanpassen"}
              </button>
            ) : null}
          </span>
        </div>

        <div className="wp-tegel-body" id="wp-tegel-body" hidden={!open}>
          {tab === "klant" &&
          !isAfgerond &&
          data?.voorstel_stand === "afronden" &&
          data.afronden_reden &&
          zaakId ? (
            <AfrondVoorstel
              staat={afronden}
              reden={data.afronden_reden}
              label={data.afronden_label ?? data.afronden_reden}
              door={kanaal === "mail" ? "Sanne" : "Nova"}
              onTochAntwoorden={() => tochAntwoorden.mutate(zaakId)}
              tochBezig={tochAntwoorden.isPending}
            />
          ) : null}
          {tab === "klant" ? (
            <>
              {kanaal === "mail" ? (
                <>
                  <div className="wp-tegel-adres">
                    <div>
                      <label htmlFor="wp-aan">Aan</label>
                      <input
                        id="wp-aan"
                        type="text"
                        value={aan}
                        onChange={(e) => setAan(e.target.value)}
                        placeholder="e-mailadres"
                      />
                      <span style={{ display: "flex", gap: 4 }}>
                        <button
                          type="button"
                          className="wp-knop wp-tegel-knop-klein"
                          aria-pressed={ccAan}
                          onClick={() => setCcAan(!ccAan)}
                        >
                          + CC
                        </button>
                        <button
                          type="button"
                          className="wp-knop wp-tegel-knop-klein"
                          aria-pressed={bccAan}
                          onClick={() => setBccAan(!bccAan)}
                        >
                          + BCC
                        </button>
                      </span>
                    </div>
                    {ccAan ? (
                      <div>
                        <label htmlFor="wp-cc">CC</label>
                        <input
                          id="wp-cc"
                          type="text"
                          value={cc}
                          onChange={(e) => setCc(e.target.value)}
                          placeholder="e-mailadres"
                        />
                      </div>
                    ) : null}
                    {bccAan ? (
                      <div>
                        <label htmlFor="wp-bcc">BCC</label>
                        <input
                          id="wp-bcc"
                          type="text"
                          value={bcc}
                          onChange={(e) => setBcc(e.target.value)}
                          placeholder="e-mailadres"
                        />
                      </div>
                    ) : null}
                  </div>
                  <div className="wp-tegel-onderwerp">
                    <label htmlFor="wp-onderwerp" className="wp-sr">
                      Onderwerp
                    </label>
                    <input
                      id="wp-onderwerp"
                      type="text"
                      value={onderwerp}
                      onChange={(e) => setOnderwerp(e.target.value)}
                      placeholder="Onderwerp"
                    />
                  </div>
                </>
              ) : null}

              {isAfgerond ? (
                <p
                  className="wp-tegel-verstuurd-kop"
                  data-testid="wp-tegel-afgerond"
                >
                  {kopTekst}
                </p>
              ) : null}

              {!isAfgerond && data?.reden_leeg && !tekst ? (
                <p className="wp-tegel-uitleg">{data.reden_leeg}</p>
              ) : null}

              <label
                htmlFor="wp-tekst"
                className="wp-sr"
                hidden={afgerondZonderTekst || zonderGesprek}
              >
                Bericht aan de klant
              </label>
              {/* Sessie 48 (A): bij een telefoonzaak valt er niets te
                  versturen; een leeg tekstveld zou dat wel beloven. */}
              <textarea
                hidden={afgerondZonderTekst || zonderGesprek}
                id="wp-tekst"
                ref={tekstRef}
                className="wp-tegel-veld"
                data-bewerken={bewerken && !isAfgerond ? "ja" : "nee"}
                value={tekst}
                readOnly={
                  isAfgerond || (Boolean(data?.tekst) && !bewerken && !netjes)
                }
                onChange={(e) => {
                  setTekst(e.target.value);
                  if (netjes) setNetjes(null);
                }}
                onKeyDown={toetsInTekstveld}
                rows={4}
                placeholder="Typ of dicteer je antwoord en druk op Enter. Plak gerust een lange tekst."
              />

              {netjes ? (
                <div className="wp-tegel-netjes">
                  <div className="wp-tegel-netjes-kop">
                    <span>
                      Netjes gemaakt, inhoud en toon gelijk. Jij keurt goed.
                    </span>
                    <span className="wp-chip wp-chip-grijs">
                      stem {stemLabel(stem ?? "jp")}
                    </span>
                  </div>
                  {netjes.netjes}
                </div>
              ) : null}

              {netjesMutatie.isError ? (
                <p className="wp-tegel-fout">
                  {foutTekst(netjesMutatie.error)}
                </p>
              ) : null}

              {data?.handtekening_tekst ? (
                <div className="wp-tegel-hand">{data.handtekening_tekst}</div>
              ) : null}

              {data && data.bijlagen.length > 0 ? (
                <div style={{ marginTop: 8 }}>
                  <div style={{ fontSize: 11, color: "var(--wp-tekst3)" }}>
                    Bijlagen bij deze zaak
                  </div>
                  {data.bijlagen.map((bijlage) => (
                    <label
                      key={bijlage.id}
                      className="wp-tegel-leert"
                      style={{ marginTop: 4 }}
                    >
                      <input
                        type="checkbox"
                        checked={bijlageIds.includes(bijlage.id)}
                        onChange={(e) =>
                          setBijlageIds((huidig) =>
                            e.target.checked
                              ? [...huidig, bijlage.id]
                              : huidig.filter((id) => id !== bijlage.id),
                          )
                        }
                      />
                      {bijlage.naam}
                    </label>
                  ))}
                </div>
              ) : null}

              {aangepast ? (
                <label className="wp-tegel-leert">
                  <input
                    type="checkbox"
                    checked={sanneLeert}
                    onChange={(e) => setSanneLeert(e.target.checked)}
                  />
                  Sanne leert van deze aanpassing
                </label>
              ) : null}
            </>
          ) : (
            <>
              <label htmlFor="wp-notitie" className="wp-sr">
                Interne notitie
              </label>
              <textarea
                id="wp-notitie"
                className="wp-tegel-veld"
                value={notitie}
                onChange={(e) => setNotitie(e.target.value)}
                rows={4}
                placeholder="Interne notitie bij deze zaak. Komt op de tijdlijn, Sanne leest mee."
              />
              {notitieMutatie.isError ? (
                <p className="wp-tegel-fout">
                  {foutTekst(notitieMutatie.error)}
                </p>
              ) : null}
            </>
          )}
        </div>

        <div className="wp-tegel-rij" hidden={!open}>
          {tab === "klant" ? (
            <>
              {/* De reden staat in de tip en in een tekst die alleen
                  een schermlezer krijgt (JP, punt 22 van 22-09). In
                  beeld is de rij nu links Bijlage en Stem, rechts het
                  moment en de knoppen — zonder hulpzinnen ertussen. */}
              {/* Sessie 48 (Simulator): bij een telefoonzaak geen bijlage en
                  geen stem; er gaat niets naar de klant. */}
              {zonderGesprek ? null : (
                <>
              <button
                type="button"
                className="wp-knop wp-tegel-knop-klein"
                disabled
                title="Een eigen bestand kan nog niet; bijlagen van de zaak vink je hierboven aan."
                aria-describedby="wp-tegel-bijlage-reden"
              >
                Bijlage toevoegen
              </button>
              <span id="wp-tegel-bijlage-reden" className="wp-sr-only">
                Een eigen bestand kan nog niet; bijlagen van de zaak vink je
                hierboven aan.
              </span>
              <span className="wp-tegel-rij-label">Stem</span>
              {stemmen.map((welke) => (
                <button
                  key={welke}
                  type="button"
                  className="wp-knop wp-tegel-knop-klein"
                  aria-pressed={stem === welke}
                  onClick={() => {
                    setStem(welke);
                    handtekeningMutatie.mutate(welke);
                  }}
                >
                  {stemLabel(welke)}
                </button>
              ))}
                </>
              )}
              <button
                type="button"
                className="wp-knop wp-tegel-knop-klein"
                onClick={() => setBelvensterOpen(!belvensterOpen)}
                aria-pressed={belvensterOpen}
              >
                Bellen
              </button>

              {/* Alleen hier als de tegel open is: ingeklapt staan deze
                  knoppen op de kopregel, en twee keer "Alleen
                  versturen" in het scherm is één keer te veel. */}
              {open ? rechterKnoppen : null}

              {/* Dezelfde reden, maar alleen voor een schermlezer: in
                  beeld hangt hij als tip aan de knop die hij uitlegt
                  (JP, punt 22 van 22-09). Ingeklapt staat hij nog
                  steeds op de kopregel. */}
              {!open ? null : isAfgerond ? (
                <span className="wp-sr-only">
                  Het antwoord op deze zaak is al verstuurd. Nog iets sturen kan
                  met de knop hiernaast; die begint een nieuw bericht.
                </span>
              ) : redenUit ? (
                <span className="wp-sr-only">{redenUit}</span>
              ) : null}
              {plekMelding ? (
                <p className="wp-tegel-fout" role="alert" style={{ flexBasis: "100%" }}>
                  {plekMelding}. Het bericht is niet verstuurd.
                </p>
              ) : null}
              {verstuurMutatie.isError && !dubbel ? (
                <p className="wp-tegel-fout" style={{ flexBasis: "100%" }}>
                  {foutTekst(verstuurMutatie.error)}
                </p>
              ) : null}
            </>
          ) : open ? (
            rechterKnoppen
          ) : null}
        </div>
      </div>
    </div>
  );
}

/** "a@x.nl, b@x.nl" -> ["a@x.nl", "b@x.nl"], lege stukken eruit. */
function splits(waarde: string): string[] {
  return waarde
    .split(/[,;]/)
    .map((deel) => deel.trim())
    .filter(Boolean);
}
